"use client";
import { get, omitBy } from "lodash/fp";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { useMediaContext } from "../../../context/MediaContext";
import { MediaServices } from "../../../core/services/MediaServices";
import { MediaFields, MediaListParams } from "./MediaDataTypes";
import "./Medias.css";
import { isEmptyVal, displayField } from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import MediaSingleAction from "./MediaSingleAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import { appConstObj } from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import MediaPlayerFormatter from "../../components/Elements/MediaPlayerFormatter";
import MediaPlayer from "../../components/Elements/MediaPlayer";

export const Medias = () => {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<MediaFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();

  const initSearchFormData = {
    name: "",
    singer: "",
    song_type: "",
    media_type: "",
    album: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] = useState<MediaListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const navigate = useNavigate();

  async function getMedias(params: MediaListParams) {
    try {
      setLoading(true);
      const res = await MediaServices.getMedias(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function init() {
    setShowCreate(true);
  }

  useEffect(() => {
    init();
    getMedias(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
  }, []);

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: MediaListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getMedias(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getMedias(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      }),
    );
  }

  const filterMedias = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getMedias(
        getSearchParams({ smart_search: smartSearchInput, rows: rows }),
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getMedias(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      }),
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getMedias(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
  };

  return (
    <div className="songs">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>
                {t("MEDIA")} {t("LIBRARY")}
              </h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button
                  appearance="primary"
                  onClick={() => navigate(ContentRoutes.CREATE_MEDIA)}
                >
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content className="songs">
          <>
            <div className="row">
              <div className="col-md-4">
                <InputGroup>
                  <Input
                    className="form-control"
                    placeholder={t("SMART_SEARCH_BY", {
                      entity:
                        t("MEDIA").toLowerCase() +
                        " " +
                        t("NAME").toLowerCase() +
                        ", " +
                        t("SINGER").toLowerCase() +
                        ", " +
                        t("OR").toLowerCase() +
                        " " +
                        t("NAME").toLowerCase(),
                    })}
                    value={smartSearchInput}
                    onChange={(val: any) => setSmartSearch(val)}
                    onKeyDown={(e: any) => filterMedias(e)}
                  />
                  <InputGroup.Button
                    title={t("SEARCH")}
                    onClick={(e: any) => filterMedias(e)}
                  >
                    <SearchIcon />
                  </InputGroup.Button>
                  <InputGroup.Button
                    title={t("CLEAR")}
                    onClick={() => clearSmartSearch()}
                  >
                    <CloseIcon />
                  </InputGroup.Button>
                </InputGroup>
              </div>
            </div>
          </>

          <div className={total > 0 ? "song-table" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              <Column width={60} align="center" fixed>
                <CustomHeaderCell>{t("PLAY")}</CustomHeaderCell>
                <MediaPlayerFormatter
                  rowData={(rowData: any) => rowData}
                  mediaUrlKey={"media_url"}
                  mediaImageKey={"media_image"}
                />
              </Column>
              <Column width={200} sortable resizable fixed flexGrow={1}>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="title">
                  {(rowData: any) => (
                    <span title={`${rowData.title}`}>
                      {displayField(rowData.title)}
                    </span>
                  )}
                </CustomCell>
              </Column>

              <Column width={160}>
                <CustomHeaderCell>{t("SINGER")}</CustomHeaderCell>
                <CustomCell dataKey="singer1_name">
                  {(rowData: any) => (
                    <span>{displayField(rowData.singer1_name)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={200} sortable resizable>
                <CustomHeaderCell>{t("ALBUM")}</CustomHeaderCell>
                <CustomCell dataKey="album_name">
                  {(rowData: any) => (
                    <span title={`${rowData.album_name}`}>
                      {displayField(rowData.album_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100} sortable resizable>
                <CustomHeaderCell>{t("TYPE")}</CustomHeaderCell>
                <CustomCell dataKey="song_type">
                  {(rowData: any) => (
                    <span>{displayField(rowData.song_type)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={150} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => <MediaSingleAction mediaData={rowData} />}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <MediaPlayer />
        <ToastContainer />
      </Container>
    </div>
  );
};
export default Medias;
